import Head from 'next/head';
import Image from 'next/image';
import { useMemo } from 'react';
import Container from '../../components/container';
import Layout from '../../components/layout';
import NavBar from '../../components/navbar';
import config from '../../lib/config';
import useSWR from 'swr';

import { GetStaticProps, GetStaticPaths } from 'next';
import { LinkButton } from '../../components/link-button';
import { H2, Label } from '../../components/text';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../../lib/api';

import SocialLinks from '@/components/social-icons-links';
import SquadCardList from '@/components/squad-card-list';
import TeamRelatedNews from '@/components/team-related-news';
import TeamVideoEmbed from '@/components/team-video-embed';
import { IMatch } from '@/models/match';
import { IMenuItem } from '../../models/menu-item';
import { ISponsor } from '../../models/sponsor';
import { ITeam, ITeamData } from '../../models/team';
import filterTeams from '../../utils/filterHistoricalTeams';
import SquadMatchesList from './squad-matches-list';
import TeamRssFeed from '@/components/team-rss-feed';
import { Tab } from '@headlessui/react';
import RoundsTable from '@/components/rounds-table';
import { IMatchRoundData } from '@/models/matchRoundData';
import TeamStandings from '@/components/team-standings';

interface ITeamProps {
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	team: ITeamData;
	pastMatchesData: IMatch[];
	upcomingMatchesData: IMatch[];
}

// SWR fetcher for JSON endpoints
const fetcher = (url: string) => fetch(url).then((res) => res.json());

export default function TeamPage({
	primaryMenuItems,
	sponsors,
	team,
	pastMatchesData,
	upcomingMatchesData,
}: ITeamProps) {
	// Filter players by type coach and assistant coach
	const coachesAndAssistantsCoaches =
		team?.contestantPlayers?.filter(
			(player) => player.passport.type === 'assistant coach' || player.passport.type === 'coach',
		) || [];

	// Filter players by position
	const filterByTypeKeepers =
		team?.contestantPlayers?.filter((player) => player.passport.position === 'Goalkeeper') || [];

	const filterByTypeDefenders =
		team?.contestantPlayers?.filter((player) => player.passport.position === 'Defender') || [];

	const filterByTypeMidfielders =
		team?.contestantPlayers?.filter((player) => player.passport.position === 'Midfielder') || [];

	const filterByTypeAttackers =
		team?.contestantPlayers?.filter((player) => player.passport.position === 'Attacker') || [];

	// Normalize the team name to construct a tag slug.
	const normalizedTagSlug = useMemo(() => {
		return team.contestantClubName.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');
	}, [team.contestantClubName]);

	const pageTitle = `${team.contestantClubName} | ${config.siteName}`;
	const meta = team.contestantClubName || 'Teams van de Keuken Kampioen Divisie.';

	// Create a minimal IMatchRoundData object for RoundsTable
	const upcomingMatchesRoundData: IMatchRoundData = {
		matches: upcomingMatchesData?.slice(0, 5), // Only first 5 matches
		currentRound: 0,
		prevRound: 0,
		nextRound: 0,
		start_date: '',
		end_date: '',
	};

	// Client-side fetch for standings
	const { data: clientStandings, error: standingsError } = useSWR(
		`${config.apiUrl}/wp-json/statsperform/v1/standings`,
		fetcher,
		{ revalidateOnFocus: false },
	);

	return (
		<Layout sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta} key={team.slug}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Tab.Group>
				<header className="relative overflow-hidden h-[420px]">
					{team.contestantPoster?.large && (
						<Image
							src={team.contestantPoster?.large}
							alt={`${team.contestantClubName} team poster`}
							width={1920}
							height={430}
							className="object-cover h-[420px] w-full"
						/>
					)}
					<div className="kkd-gradient-red-overlay-vertical h-full w-full" aria-hidden="true"></div>
					<Container>
						<div className="absolute top-1/2 transform -translate-y-1/2 flex flex-col">
							<div className="flex items-center">
								<div className="flex-grow">
									<H2 className="text-white mb-2 !text-3xl md:!text-5xl lg:!text-6xl">
										{team.contestantClubName}
									</H2>
									{team.contestantVenueName && (
										<Label className="text-white">{team.contestantVenueName}</Label>
									)}
								</div>
								{team.contestantLogo && (
									<Image
										src={team.contestantLogo}
										alt={`${team.contestantClubName} logo`}
										width={120}
										height={120}
										className="ml-8"
									/>
								)}
							</div>
							<nav className="mt-4 flex" aria-label="Team actions">
								<div className="mr-4">
									<LinkButton
										size="small"
										href={team.contestantTicketURL}
										target="_blank"
										icon="exit-link"
										variant="ghost-inverted"
									>
										Tickets
									</LinkButton>
								</div>
								<div className="mr-4">
									<LinkButton
										size="small"
										href={team.contestantWebsiteURL}
										target="_blank"
										icon="exit-link"
										variant="ghost-inverted"
									>
										Website
									</LinkButton>
								</div>
							</nav>
							{team.contestantSocialLinks && (
								<nav className="mt-4" aria-label="Social media links">
									<SocialLinks
										socialLinks={team.contestantSocialLinks}
										className="flex mt-4 gap-4 mb-4"
									/>
								</nav>
							)}
						</div>
						<Tab.List className="absolute bottom-0 flex gap-4">
							<>
								<Tab
									className={({ selected }) =>
										`min-w-32 -translate-y-[2px] focus:outline-none ${
											selected ? 'shadow-[0_2px_0_0_rgb(255,255,255)]' : ''
										}`
									}
								>
									<Label className={`p-2 mr-2 text-white font-bold`}>Overzicht</Label>
								</Tab>
								<Tab
									className={({ selected }) =>
										`min-w-32 -translate-y-[2px] focus:outline-none ${
											selected ? 'shadow-[0_2px_0_0_rgb(255,255,255)]' : ''
										}`
									}
								>
									<Label className={`p-2 mr-2 text-white `}>Team</Label>
								</Tab>
								<Tab
									className={({ selected }) =>
										`min-w-32 -translate-y-[2px] focus:outline-none ${
											selected ? 'shadow-[0_2px_0_0_rgb(255,255,255)]' : ''
										}`
									}
								>
									<Label className={`p-2 mr-2 text-white `}>Programma</Label>
								</Tab>
								<Tab
									className={({ selected }) =>
										`min-w-32 -translate-y-[2px] focus:outline-none ${
											selected ? 'shadow-[0_2px_0_0_rgb(255,255,255)]' : ''
										}`
									}
								>
									<Label className={`p-2 mr-2 text-white `}>Resultaten</Label>
								</Tab>
							</>
						</Tab.List>
					</Container>
				</header>
				<Container className="py-12 xl:py-16">
					<Tab.Panels>
						<Tab.Panel>
							<section className="mb-12">
								<Container>
									<div className="flex flex-col lg:flex-row">
										<aside className="w-full lg:w-4/12 xl:w-3/12 h-full mb-12">
											{/* You can add any sidebar content here if needed */}
											{upcomingMatchesData && (
												<div className="bg-black-500 mb-12">
													<div className="px-6 py-8 text-center">
														<Label className="text-white font-bold">Programma</Label>
													</div>
													<RoundsTable matchesData={upcomingMatchesRoundData} snug />
												</div>
											)}
											{clientStandings && clientStandings.length > 0 && (
												<TeamStandings
													standings={clientStandings}
													currentTeamSlug={team.contestantClubName}
												/>
											)}
											{standingsError && (
												<div className="text-red-500 text-sm px-6 py-2">
													Standings konden niet worden geladen.
												</div>
											)}
										</aside>
										<div className="flex-1 lg:pl-6 xl:pl-16 w-full lg:w-8/12 xl:w-9/12">
											<TeamRelatedNews tagSlug={normalizedTagSlug} />
											<TeamRssFeed
												logo={team.contestantLogo}
												feedUrl={team.rssFeedURL || undefined}
												colorCode={team.colorCode || undefined}
											/>

											<TeamVideoEmbed contestantCode={team.contestantCode} />
										</div>
									</div>
								</Container>
							</section>
						</Tab.Panel>
						<Tab.Panel>
							<SquadCardList
								coaches={coachesAndAssistantsCoaches}
								goalkeepers={filterByTypeKeepers}
								defenders={filterByTypeDefenders}
								midfielders={filterByTypeMidfielders}
								attackers={filterByTypeAttackers}
							/>
						</Tab.Panel>
						<Tab.Panel>
							<H2>Programma</H2>
							<SquadMatchesList matches={upcomingMatchesData} />
						</Tab.Panel>
						<Tab.Panel>
							<H2>Resultaten</H2>
							<SquadMatchesList matches={pastMatchesData} />
						</Tab.Panel>
						<Tab.Panel>
							<H2>Team stats</H2>
							{/* <SeasonTeamStats /> */}
						</Tab.Panel>
					</Tab.Panels>
				</Container>
			</Tab.Group>
		</Layout>
	);
}

export const getStaticPaths: GetStaticPaths = async () => {
	try {
		const response = await fetch(
			`${process.env.NEXT_PUBLIC_API_URL}/wp-json/wp/v2/teams?per_page=40&_fields=id,slug`,
		);
		const teams: ITeam[] = await response.json();
		const filtered = filterTeams(teams);
		const paths = filtered
			.filter((team) => typeof team.slug === 'string' && team.slug.length > 0)
			.map((team) => ({ params: { slug: team.slug } }));
		return { paths, fallback: 'blocking' };
	} catch {
		return { paths: [], fallback: 'blocking' };
	}
};

export const getStaticProps: GetStaticProps<ITeamProps> = async (context) => {
	const { params, preview = false } = context;
	const teamSlug = Array.isArray(params?.slug) ? params.slug[0] : params?.slug;

	if (!teamSlug) {
		return { notFound: true };
	}

	try {
		const teamDataEndpoint = `${config.apiUrl}/wp-json/statsperform/v1/team/${teamSlug}`;
		const teamResponse = await fetch(teamDataEndpoint);

		if (!teamResponse.ok) {
			return {
				redirect: {
					destination: '/teams',
					statusCode: 302,
				},
				revalidate: 60,
			};
		}

		const teamData: ITeamData = await teamResponse.json();

		const [primaryMenuItems, sponsorData, rawPastMatchesData, upcomingMatchesData] = await Promise.all([
			getPrimaryMenuItems(),
			fetchAPI<{ sponsors: { nodes: any[] } }>(`
                query {
                    sponsors(first: 20) {
                        nodes {
                            title
                            sponsor_fields {
                                subtitle
                                websiteUrl
                                mainSponsor
                                invertedImage {
                                    sourceUrl
                                }
                            }
                        }
                    }
                }`),
			fetchData(
				`${config.apiUrl}/wp-json/statsperform/v1/team-past-matches?team=${encodeURIComponent(
					teamData.contestantClubSlug,
				)}`,
			),
			fetchData(
				`${config.apiUrl}/wp-json/statsperform/v1/team-upcoming-matches?team=${encodeURIComponent(
					teamData.contestantClubSlug,
				)}`,
			),
		]);

		const pastMatchesData = Array.isArray(rawPastMatchesData) ? rawPastMatchesData.slice(0, 10) : [];

		return {
			props: {
				sponsors: sponsorData.sponsors.nodes,
				preview: preview || false,
				primaryMenuItems,
				team: teamData,
				pastMatchesData,
				upcomingMatchesData,
			},
			revalidate: 1800,
		};
	} catch (error) {
		console.error('Error in getStaticProps:', error);
		return { notFound: true, revalidate: 60 };
	}
};
